import Logger from "../../../clients/sentry";
import { fetchEstimateGroupings, fetchSOVGroupings, fetchSortFieldById, fetchVMGroupings } from "../../../api";
import { serializeErrorForWarning } from "../../../errors";
import { SortField } from "../../../interfaces/SortField";
import { parseDateValue } from "../../utils/displayValue";
import { safeSortFieldColumnName } from "../../utils/sortCodes";
import { getNamedRangeFromWorksheet } from "../../workbook";
import { getProcessedEstimate } from "../../datasources/estimate";
import { rangeNames } from "./rangeNames";
import { browserStore } from "../../../utils/storageKeys";

type SortFieldNames = {
  sortFieldColumnNameMap: Record<string, string>;
  labelSortFieldColumnNameMap: Record<string, string>;
  usedSortFields: { [key: string]: SortField };
};

const NO_SORT_FIELD_NAMES: SortFieldNames = {
  sortFieldColumnNameMap: {},
  labelSortFieldColumnNameMap: {},
  usedSortFields: {},
};

export const groupDataByKey = (
  data: any[],
  key: string,
  labelKey?: string
): Record<string, { data: any[]; label: any }> => {
  const groups: Record<string, { data: any[]; label: any }> = {};
  data.forEach((item) => {
    const groupKey = item[key] ?? "Undefined";
    if (!groups[groupKey]) {
      groups[groupKey] = {
        data: [],
        label: labelKey ? item[labelKey] : item[key],
      };
    }
    groups[groupKey].data.push(item);
  });
  return groups;
};

/** The loaded estimate's sort-field column names, so groupings resolve without a request. */
const sortFieldNamesFor = async (estimateId: string): Promise<SortFieldNames> => {
  const processed = await getProcessedEstimate(estimateId).catch(() => undefined);
  return processed ?? NO_SORT_FIELD_NAMES;
};

export const getEstimateGroupings = async (estimateId: string): Promise<string[]> => {
  try {
    const result = await fetchEstimateGroupings(estimateId);
    if (!result.success) {
      throw result.error;
    }

    // Filter out empty groupings
    const rawGroupings = Object.values(result.data?.data?.value || {}).filter(
      (grouping) => grouping !== ""
    ) as string[];

    const names = await sortFieldNamesFor(estimateId);
    const groupings = await Promise.all(
      rawGroupings.map((grouping) => findSortFieldNameFromGroupByProp(grouping, names))
    );

    return groupings.filter((grouping): grouping is string => grouping !== null);
  } catch (error) {
    Logger.warn("Error processing estimate groupings", serializeErrorForWarning(error));
    return ["mf1", "mf2", "mf3"];
  }
};

export const getVMGroupings = async (estimateId: string): Promise<string[]> => {
  try {
    const result = await fetchVMGroupings(estimateId);
    if (!result.success) {
      throw result.error;
    }

    // Filter out empty groupings
    const rawGroupings = Object.values(result.data?.data?.value || {}).filter(
      (grouping) => grouping !== ""
    ) as string[];

    return rawGroupings;
  } catch (error) {
    Logger.warn("Error processing VM groupings", serializeErrorForWarning(error));
    return ["status"];
  }
};

export const getSOVGroupings = async (estimateId: string): Promise<string> => {
  const defaultGroup = "Bid Package";
  const storedUserData = browserStore.get("userProfile") ?? "";
  const parsedData = storedUserData ? JSON.parse(storedUserData) : {};

  const userId = parsedData?.id ?? null;

  if (!userId) {
    return defaultGroup;
  }

  const result = await fetchSOVGroupings(userId);
  if (!result.success) {
    Logger.warn("Failed to fetch SOV grouping", serializeErrorForWarning(result.error));
    return defaultGroup;
  }

  const grouping = await findSortFieldNameFromGroupByProp(
    result.data?.data?.value || defaultGroup,
    await sortFieldNamesFor(estimateId)
  );
  return grouping || defaultGroup;
};

export const addGroupingMetaData = async (worksheet: Excel.Worksheet, groupByProps: string[]) => {
  try {
    const groupings = readableGroupings(groupByProps).join(" > ");
    const range = await getNamedRangeFromWorksheet(worksheet, rangeNames.reportGrouping);
    if (range) {
      range.values = [[groupings]];
      await worksheet.context.sync();
    }
  } catch (error) {
    console.error("Error adding grouping metadata to worksheet", error);
  }
};

export function normalizeGroupKey(
  value: string | number | null | undefined,
  groupByProp?: string,
  blankLabel = "Blank"
): string {
  if (value == null || (typeof value === "string" && value === "")) {
    return blankLabel;
  }

  const isDateGroupingField = typeof groupByProp === "string" && /(_at|_date)$/i.test(groupByProp);
  if (isDateGroupingField) {
    const date = parseDateValue(value);
    return date ? date.toISOString().slice(0, 10) : String(value);
  }

  return String(value);
}
const findSortFieldNameFromGroupByProp = async (
  groupByProp: string,
  { sortFieldColumnNameMap, labelSortFieldColumnNameMap, usedSortFields }: SortFieldNames
): Promise<string> => {
  if (groupByProp === "estimate_use_groups_labels") {
    return "use_group";
  } else if (groupByProp.startsWith("label_sort_field_")) {
    const sortId = groupByProp.slice(17);
    if (labelSortFieldColumnNameMap[sortId]) return labelSortFieldColumnNameMap[sortId];
    if (sortFieldColumnNameMap[sortId]) return `label_${sortFieldColumnNameMap[sortId]}`;
    const rawName = usedSortFields[sortId]?.name;
    if (rawName) return `label_${safeSortFieldColumnName(rawName)}`;
    const result = await fetchSortFieldById(sortId);
    if (!result.success) {
      Logger.warn("Failed to fetch sort field for label", serializeErrorForWarning(result.error));
      return groupByProp;
    }
    return `label_${safeSortFieldColumnName(result.data?.name || groupByProp)}`;
  } else if (groupByProp.slice(0, 11) === "sort_field_") {
    const sortId = groupByProp.slice(11);
    if (sortFieldColumnNameMap[sortId]) return sortFieldColumnNameMap[sortId];
    const rawName = usedSortFields[sortId]?.name;
    if (rawName) return safeSortFieldColumnName(rawName);
    const result = await fetchSortFieldById(sortId);
    if (!result.success) {
      Logger.warn("Failed to fetch sort field", serializeErrorForWarning(result.error));
      return groupByProp;
    }
    return safeSortFieldColumnName(result.data?.name || groupByProp);
  } else {
    return groupByProp;
  }
};

const readableGroupings = (groupings: string[]) => {
  return groupings.map((grouping) => {
    if (grouping === "use_group") {
      return "Use Group";
    } else if (grouping === "alternate") {
      return "Alternates";
    } else if (/^mf\d+/.test(grouping)) {
      return grouping.replace(/^mf(\d+)/, "MF$1");
    } else if (/^uf\d+/.test(grouping)) {
      return grouping.replace(/^uf(\d+)/, "UF$1");
    } else if (grouping.startsWith("label_")) {
      return `${grouping.replace(/^label_/, "")} - Labels`;
    }
    return grouping; // Return as-is if no condition matches
  });
};
